/**
 * Notifications Store
 *
 * Manages real-time notifications for the current user
 * with support for marking as read and unread counts
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { db } from '@/js/firebase';
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  limit,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { logger } from '@/services/loggerService';

export const useNotificationsStore = defineStore('notifications', () => {
  //-------------------------------
  // STATE
  //-------------------------------

  /**
   * All notifications for current user
   * @type {Array}
   */
  const items = ref([]);

  /**
   * Loading state
   * @type {Boolean}
   */
  const loading = ref(false);

  /**
   * Store initialization state
   * @type {Boolean}
   */
  const initialized = ref(false);

  /**
   * Error state
   * @type {String|null}
   */
  const error = ref(null);

  /**
   * Current user ID for the subscription
   * @type {String|null}
   */
  const currentUserId = ref(null);

  /**
   * Company filter for multi-company users
   * null = show all (admin view), string = filter by company ID (client view)
   * @type {String|null}
   */
  const filterByCompanyId = ref(null);

  /**
   * Unsubscribe function for notifications listener
   * @type {Function|null}
   */
  let notificationsUnsubscribe = null;

  /**
   * Audio element for notification sound
   * @type {HTMLAudioElement|null}
   */
  let notificationSound = null;

  /**
   * Track known notification IDs to detect new ones
   * @type {Set<string>}
   */
  let knownNotificationIds = new Set();

  /**
   * Flag to track if this is the initial load
   * @type {Boolean}
   */
  let isInitialLoad = true;

  //-------------------------------
  // HELPERS
  //-------------------------------

  /**
   * Play notification sound
   */
  function playNotificationSound() {
    try {
      // Create audio element if not exists
      if (!notificationSound) {
        notificationSound = new Audio('/assets/chat-message.wav');
        notificationSound.volume = 0.5;
      }
      // Reset to start and play
      notificationSound.currentTime = 0;
      notificationSound.play().catch((err) => {
        // Ignore autoplay errors (browser may block until user interaction)
        logger.debug('[Notifications] Could not play sound:', err.message);
      });
    } catch (err) {
      logger.debug('[Notifications] Error creating audio:', err.message);
    }
  }

  //-------------------------------
  // GETTERS
  //-------------------------------

  /**
   * Get filtered items based on company filter
   * If filterByCompanyId is null, returns all items (admin view)
   * If filterByCompanyId is set, returns only items for that company (client view)
   */
  const filteredItems = computed(() => {
    if (!filterByCompanyId.value) {
      return items.value;
    }
    return items.value.filter(n => n.companyId === filterByCompanyId.value);
  });

  /**
   * Get unread notifications count (respects company filter)
   */
  const unreadCount = computed(() => {
    return filteredItems.value.filter(n => !n.isRead).length;
  });

  /**
   * Get total unread count (ignores company filter - for showing "all" count)
   */
  const totalUnreadCount = computed(() => {
    return items.value.filter(n => !n.isRead).length;
  });

  /**
   * Get unread notifications (respects company filter)
   */
  const unreadNotifications = computed(() => {
    return filteredItems.value.filter(n => !n.isRead);
  });

  /**
   * Get notifications grouped by date (respects company filter)
   */
  const groupedByDate = computed(() => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    filteredItems.value.forEach(notification => {
      let dateKey;
      const notifDate = notification.createdAt?.toDate?.() || new Date(notification.createdAt);
      const notifDateOnly = new Date(notifDate);
      notifDateOnly.setHours(0, 0, 0, 0);

      if (notifDateOnly.getTime() === today.getTime()) {
        dateKey = 'Today';
      } else if (notifDateOnly.getTime() === yesterday.getTime()) {
        dateKey = 'Yesterday';
      } else {
        dateKey = notifDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: notifDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
      }

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(notification);
    });

    return groups;
  });

  /**
   * Check if there are any notifications (respects company filter)
   */
  const hasNotifications = computed(() => {
    return filteredItems.value.length > 0;
  });

  /**
   * Check if there are any notifications total (ignores company filter)
   */
  const hasTotalNotifications = computed(() => {
    return items.value.length > 0;
  });

  //-------------------------------
  // ACTIONS
  //-------------------------------

  /**
   * Initialize the store for a specific user
   * @param {String} userId - The user ID to listen for notifications
   */
  async function initialize(userId) {
    if (!userId) {
      logger.warn('[Notifications] Cannot initialize without userId');
      return;
    }

    // If already initialized for this user, skip
    if (initialized.value && currentUserId.value === userId) {
      logger.info('[Notifications] Store already initialized for user', { userId });
      return;
    }

    // If initialized for different user, reset first
    if (initialized.value && currentUserId.value !== userId) {
      logger.info('[Notifications] Switching user, resetting store');
      reset();
    }

    try {
      loading.value = true;
      error.value = null;
      currentUserId.value = userId;

      // Set up real-time listener for user's notifications
      const notificationsRef = collection(db, 'users', userId, 'notifications');
      const q = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(50) // Limit to most recent 50 notifications
      );

      notificationsUnsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const notifications = [];

          snapshot.forEach(doc => {
            notifications.push({
              id: doc.id,
              ...doc.data()
            });
          });

          // Check for new notifications (only after initial load)
          if (!isInitialLoad) {
            const newNotifications = notifications.filter(
              n => !knownNotificationIds.has(n.id) && !n.isRead
            );

            logger.debug('[Notifications] Checking for new notifications', {
              isInitialLoad,
              knownCount: knownNotificationIds.size,
              currentCount: notifications.length,
              newUnreadCount: newNotifications.length,
              newIds: newNotifications.map(n => n.id)
            });

            if (newNotifications.length > 0) {
              logger.info('[Notifications] New notifications received', {
                count: newNotifications.length
              });
              playNotificationSound();
            }
          } else {
            logger.debug('[Notifications] Initial load, skipping sound', {
              count: notifications.length
            });
          }

          // Update known notification IDs
          knownNotificationIds = new Set(notifications.map(n => n.id));
          isInitialLoad = false;

          items.value = notifications;

          logger.info('[Notifications] Updated', {
            count: notifications.length,
            unread: notifications.filter(n => !n.isRead).length
          });

          if (!initialized.value) {
            initialized.value = true;
          }
          loading.value = false;
        },
        (snapshotError) => {
          logger.firestoreListenerError('[Notifications] Snapshot error', snapshotError);

          // Handle missing index error - fall back to simpler query
          if (snapshotError.code === 'failed-precondition') {
            logger.warn('[Notifications] Missing index, using fallback query');

            // Try without ordering (will sort in JS)
            const fallbackQuery = query(
              notificationsRef,
              limit(50)
            );

            notificationsUnsubscribe = onSnapshot(
              fallbackQuery,
              (snapshot) => {
                const notifications = [];
                snapshot.forEach(doc => {
                  notifications.push({
                    id: doc.id,
                    ...doc.data()
                  });
                });

                // Sort manually by createdAt descending
                notifications.sort((a, b) => {
                  const dateA = a.createdAt?.toMillis?.() || 0;
                  const dateB = b.createdAt?.toMillis?.() || 0;
                  return dateB - dateA;
                });

                items.value = notifications;

                if (!initialized.value) {
                  initialized.value = true;
                }
                loading.value = false;
              }
            );
          } else {
            error.value = snapshotError.message;
            loading.value = false;
          }
        }
      );

      logger.info('[Notifications] Initialized for user', { userId });
    } catch (initError) {
      logger.error('[Notifications] Failed to initialize:', initError);
      error.value = initError.message;
      loading.value = false;
    }
  }

  /**
   * Mark a single notification as read
   * @param {String} notificationId - The notification ID to mark as read
   */
  async function markAsRead(notificationId) {
    if (!currentUserId.value) {
      logger.warn('[Notifications] Cannot mark as read - no user ID');
      return;
    }

    try {
      const notifRef = doc(db, 'users', currentUserId.value, 'notifications', notificationId);
      await updateDoc(notifRef, {
        isRead: true,
        readAt: serverTimestamp()
      });

      logger.info('[Notifications] Marked as read', { notificationId });
    } catch (markError) {
      logger.error('[Notifications] Failed to mark as read:', markError);
      throw markError;
    }
  }

  /**
   * Mark a single notification as unread
   * @param {String} notificationId - The notification ID to mark as unread
   */
  async function markAsUnread(notificationId) {
    if (!currentUserId.value) {
      logger.warn('[Notifications] Cannot mark as unread - no user ID');
      return;
    }

    try {
      const notifRef = doc(db, 'users', currentUserId.value, 'notifications', notificationId);
      await updateDoc(notifRef, {
        isRead: false,
        readAt: deleteField()
      });

      logger.info('[Notifications] Marked as unread', { notificationId });
    } catch (markError) {
      logger.error('[Notifications] Failed to mark as unread:', markError);
      throw markError;
    }
  }

  /**
   * Mark all unread notifications as read
   */
  async function markAllAsRead() {
    if (!currentUserId.value) {
      logger.warn('[Notifications] Cannot mark all as read - no user ID');
      return;
    }

    const unread = items.value.filter(n => !n.isRead);
    if (unread.length === 0) {
      logger.info('[Notifications] No unread notifications to mark');
      return;
    }

    try {
      const batch = writeBatch(db);
      const now = serverTimestamp();

      unread.forEach(notification => {
        const notifRef = doc(db, 'users', currentUserId.value, 'notifications', notification.id);
        batch.update(notifRef, {
          isRead: true,
          readAt: now
        });
      });

      await batch.commit();

      logger.info('[Notifications] Marked all as read', { count: unread.length });
    } catch (batchError) {
      logger.error('[Notifications] Failed to mark all as read:', batchError);
      throw batchError;
    }
  }

  /**
   * Delete a single notification
   * @param {String} notificationId - The notification ID to delete
   */
  async function deleteNotification(notificationId) {
    if (!currentUserId.value) {
      logger.warn('[Notifications] Cannot delete - no user ID');
      return;
    }

    try {
      const notifRef = doc(db, 'users', currentUserId.value, 'notifications', notificationId);
      await deleteDoc(notifRef);

      logger.info('[Notifications] Deleted notification', { notificationId });
    } catch (deleteError) {
      logger.error('[Notifications] Failed to delete notification:', deleteError);
      throw deleteError;
    }
  }

  /**
   * Delete all read notifications
   */
  async function deleteAllRead() {
    if (!currentUserId.value) {
      logger.warn('[Notifications] Cannot delete all read - no user ID');
      return;
    }

    const readNotifications = items.value.filter(n => n.isRead);
    if (readNotifications.length === 0) {
      logger.info('[Notifications] No read notifications to delete');
      return;
    }

    try {
      const batch = writeBatch(db);

      readNotifications.forEach(notification => {
        const notifRef = doc(db, 'users', currentUserId.value, 'notifications', notification.id);
        batch.delete(notifRef);
      });

      await batch.commit();

      logger.info('[Notifications] Deleted all read notifications', { count: readNotifications.length });
    } catch (batchError) {
      logger.error('[Notifications] Failed to delete all read:', batchError);
      throw batchError;
    }
  }

  /**
   * Get notification by ID
   * @param {String} notificationId
   * @returns {Object|null}
   */
  function getNotificationById(notificationId) {
    return items.value.find(n => n.id === notificationId) || null;
  }

  /**
   * Set company filter for multi-company users
   * @param {String|null} companyId - Company ID to filter by, or null for all
   */
  function setCompanyFilter(companyId) {
    filterByCompanyId.value = companyId;
    logger.info('[Notifications] Company filter set', { companyId });
  }

  /**
   * Clear company filter (show all notifications)
   */
  function clearCompanyFilter() {
    filterByCompanyId.value = null;
    logger.info('[Notifications] Company filter cleared');
  }

  /**
   * Reset the store
   */
  function reset() {
    // Cleanup subscription first
    if (notificationsUnsubscribe) {
      notificationsUnsubscribe();
      notificationsUnsubscribe = null;
    }

    items.value = [];
    loading.value = false;
    initialized.value = false;
    error.value = null;
    currentUserId.value = null;
    filterByCompanyId.value = null;

    // Reset notification sound tracking
    knownNotificationIds = new Set();
    isInitialLoad = true;

    logger.info('[Notifications] Store reset completed');
  }

  return {
    // State
    items,
    loading,
    initialized,
    error,
    filterByCompanyId,

    // Getters
    filteredItems,
    unreadCount,
    totalUnreadCount,
    unreadNotifications,
    groupedByDate,
    hasNotifications,
    hasTotalNotifications,

    // Actions
    initialize,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    getNotificationById,
    setCompanyFilter,
    clearCompanyFilter,
    reset
  };
});
